import { DocumentationPageApprovalState, DocumentationPageContentItem } from "@supernova-studio/model";
import { z } from "zod";
import { DTODocumentationPageApprovalState } from "../../documentation";
import { DTODocumentationDraftState } from "./draft-state";
import { DTODocumentationItemConfigurationV2 } from "./item-configuration-v2";
import { DTODocumentationPublishMetadata } from "./metadata";

//
// Read
//

export const DTODocumentationPageV2 = z.object({
  id: z.string(),
  persistentId: z.string(),

  designSystemVersionId: z.string(),

  title: z.string(),
  configuration: DTODocumentationItemConfigurationV2,

  shortPersistentId: z.string(),

  slug: z.string().optional(),
  userSlug: z.string().optional(),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),

  path: z.string(),

  /** Defined when a page has changed since last publish and can be included into a partial publish */
  draftState: DTODocumentationDraftState.optional(),

  /** Defined if a page was published at least once and contains metadata about last publish */
  publishMetadata: DTODocumentationPublishMetadata.optional(),

  /** Defines the approval state of the documentation page */
  approvalState: DTODocumentationPageApprovalState.optional(),

  /** Id of the page document room */
  liveblocksRoomId: z.string().optional(),

  // Backward compatibility
  type: z.literal("Page"),
});

export type DTODocumentationPageV2 = z.infer<typeof DTODocumentationPageV2>;

//
// Write
//

export const DTOCreateDocumentationPageInputV2 = z.object({
  // Identifier
  persistentId: z.string(),

  // Page properties
  title: z.string(),
  configuration: DTODocumentationItemConfigurationV2.partial().optional(),

  // Page placement properties
  parentPersistentId: z.string(),
  afterPersistentId: z.string().nullish(),
});

export const DTOUpdateDocumentationPageInputV2 = z.object({
  // Identifier of the page to update
  id: z.string(),

  // Page properties
  title: z.string().optional(),
  configuration: DTODocumentationItemConfigurationV2.partial().optional(),
});

export const DTOUpdateDocumentationPageDocumentInputV2 = z.object({
  // Identifier of the page to update
  id: z.string(),

  // Page properties
  documentItems: z.array(DocumentationPageContentItem),
});

export const DTOMoveDocumentationPageInputV2 = z.object({
  // Identifier of the group to update
  id: z.string(),

  // Page placement properties
  parentPersistentId: z.string(),
  afterPersistentId: z.string().nullish(),
});

export const DTODuplicateDocumentationPageInputV2 = z.object({
  // Identifier of the page to duplicate from
  id: z.string(),

  // New page persistent id
  persistentId: z.string(),

  // Page placement properties
  parentPersistentId: z.string(),
  afterPersistentId: z.string().nullish(),
});

export const DTODeleteDocumentationPageInputV2 = z.object({
  // Identifier
  id: z.string(),
});

export const DTORestoreDocumentationPageInput = z.object({
  persistentId: z.string(),
  snapshotId: z.string().optional(),
});

export const DTORestoreDocumentationGroupInput = z.object({
  persistentId: z.string(),
  snapshotId: z.string().optional(),
});

export const DTODocumentationPageApprovalStateChangeInput = z.object({
  persistentId: z.string(),
  approvalState: DocumentationPageApprovalState.optional(),
});

export type DTOCreateDocumentationPageInputV2 = z.infer<typeof DTOCreateDocumentationPageInputV2>;
export type DTOUpdateDocumentationPageInputV2 = z.infer<typeof DTOUpdateDocumentationPageInputV2>;
export type DTOUpdateDocumentationPageDocumentInputV2 = z.infer<typeof DTOUpdateDocumentationPageDocumentInputV2>;
export type DTOMoveDocumentationPageInputV2 = z.infer<typeof DTOMoveDocumentationPageInputV2>;
export type DTODuplicateDocumentationPageInputV2 = z.infer<typeof DTODuplicateDocumentationPageInputV2>;
export type DTODeleteDocumentationPageInputV2 = z.infer<typeof DTODeleteDocumentationPageInputV2>;
export type DTORestoreDocumentationPageInput = z.infer<typeof DTORestoreDocumentationPageInput>;
export type DTORestoreDocumentationGroupInput = z.infer<typeof DTORestoreDocumentationGroupInput>;
export type DTODocumentationPageApprovalStateChangeInput = z.infer<typeof DTODocumentationPageApprovalStateChangeInput>;
